/**
 * Audio utility functions for MockTest components
 * Provides beep sounds and audio context management
 */

let audioContext = null;

/**
 * Initialize and get audio context
 * @returns {AudioContext} The audio context instance
 */
const getAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Resume audio context if suspended (required for autoplay policy)
 * @returns {Promise<void>}
 */
const resumeAudioContext = async () => {
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') {
    await ctx.resume();
  }
};

/**
 * Play a beep sound with specified parameters
 * @param {number} frequency - Frequency in Hz (default: 600)
 * @param {number} duration - Duration in seconds (default: 0.2)
 * @param {number} volume - Volume level 0-1 (default: 0.3)
 * @param {string} type - Oscillator type (default: 'sine')
 * @returns {Promise<void>}
 */
export const playBeep = async (frequency = 600, duration = 0.2, volume = 0.3, type = 'sine') => {
  try {
    const ctx = getAudioContext();
    await resumeAudioContext();

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = type;
    oscillator.frequency.value = frequency;

    // Schedule slightly in the future to avoid device warm-up pops
    const startAt = ctx.currentTime + 0.03; // 30ms safety offset
    const attack = 0.03; // 30ms fade-in
    const release = 0.04; // 40ms fade-out
    const minGain = 0.0001; // use small floor to support exponential ramps

    // Smooth attack/release envelope using exponential ramps (click-free)
    try { gainNode.gain.cancelScheduledValues(0); } catch {}
    gainNode.gain.setValueAtTime(minGain, startAt);
    // Attack up to target volume
    try {
      gainNode.gain.exponentialRampToValueAtTime(Math.max(minGain, volume), startAt + attack);
    } catch {
      // Fallback for browsers that may not allow exponential to same value
      gainNode.gain.linearRampToValueAtTime(volume, startAt + attack);
    }
    // Hold at volume during sustain (duration)
    gainNode.gain.setValueAtTime(volume, startAt + attack);
    // Release to near-zero
    try {
      gainNode.gain.exponentialRampToValueAtTime(minGain, startAt + duration + release);
    } catch {
      gainNode.gain.linearRampToValueAtTime(minGain, startAt + duration + release);
    }

    // Return a promise that resolves when the beep finishes
    const done = new Promise((resolve) => {
      try { oscillator.onended = () => resolve(); } catch { resolve(); }
    });
    oscillator.start(startAt);
    oscillator.stop(startAt + duration + release + 0.005);
    await done;
  } catch (error) {
    console.error("Error playing beep:", error);
  }
};

/**
 * Play start recording beep (higher pitch, shorter duration)
 * @returns {Promise<void>}
 */
export const playStartBeep = async () => {
  await playBeep(860, 0.15, 0.4, 'sine');
};

/**
 * Play end recording beep (lower pitch, slightly longer)
 * @returns {Promise<void>}
 */
export const playEndBeep = async () => {
  await playBeep(400, 0.25, 0.4, 'sine');
};

/**
 * Play notification beep (medium pitch)
 * @returns {Promise<void>}
 */
export const playNotificationBeep = async () => {
  await playBeep(600, 0.2, 0.3, 'sine');
};

/**
 * Create a smooth progress value for audio/recording progress
 * @param {number} currentTime - Current time in seconds
 * @param {number} totalTime - Total duration in seconds
 * @param {number} smoothingFactor - Smoothing factor (0-1, default: 0.1)
 * @returns {number} Smoothed progress value (0-1)
 */
export const getSmoothProgress = (currentTime, totalTime, smoothingFactor = 0.1) => {
  if (totalTime <= 0) return 0;
  
  const rawProgress = Math.min(currentTime / totalTime, 1);
  
  // Apply smoothing to reduce jitter
  return Math.round(rawProgress * 1000) / 1000; // Round to 3 decimal places
};

/**
 * Format time in MM:SS format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Validate audio file format
 * @param {File} file - Audio file to validate
 * @returns {boolean} Whether the file is a valid audio format
 */
export const isValidAudioFile = (file) => {
  const validTypes = ['audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/webm', 'audio/ogg'];
  return validTypes.includes(file.type);
};

/**
 * Get audio file duration
 * @param {File} file - Audio file
 * @returns {Promise<number>} Duration in seconds
 */
export const getAudioDuration = (file) => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);
    
    audio.addEventListener('loadedmetadata', () => {
      URL.revokeObjectURL(url);
      resolve(audio.duration);
    });
    
    audio.addEventListener('error', (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    });
    
    audio.src = url;
  });
};

/**
 * Derive recording/playback status badge text and color
 * @param {Object} params
 * @param {boolean} params.hasAudio - Whether question has prompt audio
 * @param {number} params.initTimer - Seconds before prompt audio starts
 * @param {boolean} params.isPlayingAudio - Whether prompt audio is playing
 * @param {number} params.timer - Countdown before recording starts
 * @param {boolean} params.isRecording - Whether recording is active
 * @returns {{ text: string, color: string }}
 */
export const getRecordingStatusBadge = ({ hasAudio, initTimer, isPlayingAudio, timer, isRecording }) => {
  if (hasAudio && initTimer > 0) {
    return { text: `Beginning in ${initTimer} sec`, color: '#9e9e9e' };
  }
  if (isPlayingAudio) {
    return { text: 'Playing audio…', color: '#5c6bc0' };
  }
  if (timer > 0) {
    return { text: `Recording in ${timer} sec`, color: '#f57c00' };
  }
  if (isRecording) {
    return { text: 'Recording…', color: '#996cfe' };
  }
  return { text: 'Done', color: '#43a047' };
};

/**
 * Start a smooth, lightweight waveform visualizer for a given mic stream
 * Creates its own AudioContext to avoid interfering with global beep context
 * @param {Object} params
 * @param {MediaStream} params.stream - Microphone stream
 * @param {HTMLCanvasElement} params.canvas - Canvas element to render waveform
 * @param {Object} [params.options]
 * @param {number} [params.options.fftSize=2048]
 * @param {number} [params.options.smoothing=0.9]
 * @param {string} [params.options.lineColor="#996cfe"]
 * @param {string} [params.options.bgColor="#f6f2ff"]
 * @param {number} [params.options.lineWidth=2]
 * @returns {Promise<{ stop: Function, cleanup: Function, audioCtx: AudioContext }>} handle
 */
export const startStreamVisualizer = async ({ stream, canvas, options = {} }) => {
  const {
    fftSize = 2048,
    smoothing = 0.9,
    lineColor = '#996cfe',
    bgColor = '#f6f2ff',
    lineWidth = 2,
    style = 'bars', // 'bars' | 'wave'
    barWidth = 3,
    barGap = 2,
    mirror = true,
    origin = 'center', // 'left' | 'center'
  } = options;

  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  const ctx = new AudioContextCtor();
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = fftSize;
  analyser.smoothingTimeConstant = smoothing;
  analyser.minDecibels = -90;
  analyser.maxDecibels = -10;
  source.connect(analyser);

  let animationId = null;
  const bufferLength = analyser.frequencyBinCount;
  const timeData = new Uint8Array(bufferLength);
  const freqData = new Uint8Array(bufferLength);

  // HiDPI scaling for crisp lines
  const dpr = Math.max(1, Math.floor(window.devicePixelRatio || 1));
  const cssWidth = canvas.clientWidth || canvas.width;
  const cssHeight = canvas.clientHeight || canvas.height;
  canvas.width = cssWidth * dpr;
  canvas.height = cssHeight * dpr;
  const canvasCtx = canvas.getContext('2d');
  canvasCtx.scale(dpr, dpr);

  // Precompute style
  const gradient = canvasCtx.createLinearGradient(0, 0, canvas.width / dpr, 0);
  gradient.addColorStop(0, lineColor);
  gradient.addColorStop(1, '#7a53ff');

  const draw = () => {
    animationId = requestAnimationFrame(draw);
    canvasCtx.fillStyle = bgColor;
    canvasCtx.fillRect(0, 0, cssWidth, cssHeight);

    if (style === 'wave') {
      analyser.getByteTimeDomainData(timeData);
      canvasCtx.lineWidth = lineWidth;
      canvasCtx.strokeStyle = gradient;
      canvasCtx.beginPath();
      const sliceWidth = cssWidth / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = timeData[i] / 128.0;
        const y = (v * cssHeight) / 2;
        if (i === 0) canvasCtx.moveTo(x, y);
        else canvasCtx.lineTo(x, y);
        x += sliceWidth;
      }
      canvasCtx.lineTo(cssWidth, cssHeight / 2);
      canvasCtx.stroke();
    } else {
      analyser.getByteFrequencyData(freqData);
      const totalBarSpace = barWidth + barGap;
      canvasCtx.fillStyle = gradient;

      if (origin === 'center') {
        const halfWidth = cssWidth / 2;
        const maxBarsPerSide = Math.floor(halfWidth / totalBarSpace);
        const barsPerSide = Math.max(8, Math.min(48, maxBarsPerSide));
        const totalBars = barsPerSide; // per side
        const step = Math.max(1, Math.floor(freqData.length / (totalBars * 2)));

        for (let i = 0; i < barsPerSide; i++) {
          const idx = i * step;
          const v = freqData[idx] / 255;
          const magnitude = Math.pow(v, 0.9);
          const barH = Math.max(2, magnitude * cssHeight);
          const xRight = Math.floor(halfWidth + i * totalBarSpace);
          const xLeft = Math.floor(halfWidth - (i + 1) * totalBarSpace);

          if (mirror) {
            const half = barH / 2;
            const yTop = (cssHeight / 2) - half;
            const yBottom = (cssHeight / 2);
            canvasCtx.fillRect(xRight, yTop, barWidth, half);
            canvasCtx.fillRect(xRight, yBottom, barWidth, half);
            canvasCtx.fillRect(xLeft, yTop, barWidth, half);
            canvasCtx.fillRect(xLeft, yBottom, barWidth, half);
          } else {
            const y = cssHeight - barH;
            canvasCtx.fillRect(xRight, y, barWidth, barH);
            canvasCtx.fillRect(xLeft, y, barWidth, barH);
          }
        }
      } else {
        const availableWidth = cssWidth;
        const maxBars = Math.floor(availableWidth / totalBarSpace);
        const numBars = Math.max(16, Math.min(96, maxBars));
        const step = Math.max(1, Math.floor(freqData.length / numBars));
        for (let i = 0; i < numBars; i++) {
          const idx = i * step;
          const v = freqData[idx] / 255;
          const magnitude = Math.pow(v, 0.9);
          const barH = Math.max(2, magnitude * cssHeight);
          const x = i * totalBarSpace;
          if (mirror) {
            const half = barH / 2;
            const yTop = (cssHeight / 2) - half;
            const yBottom = (cssHeight / 2);
            canvasCtx.fillRect(x, yTop, barWidth, half);
            canvasCtx.fillRect(x, yBottom, barWidth, half);
          } else {
            const y = cssHeight - barH;
            canvasCtx.fillRect(x, y, barWidth, barH);
          }
        }
      }
    }
  };

  draw();

  const stop = () => {
    if (animationId) cancelAnimationFrame(animationId);
    animationId = null;
  };

  let cleaned = false;
  const cleanup = async () => {
    if (cleaned) return;
    cleaned = true;
    try { stop(); } catch {}
    try { source.disconnect(); } catch {}
    try {
      if (ctx && ctx.state !== 'closed') {
        await ctx.close().catch(() => {});
      }
    } catch {}
    try {
      const ctx2d = canvas.getContext('2d');
      ctx2d.clearRect(0, 0, canvas.width, canvas.height);
    } catch {}
  };

  return { stop, cleanup, audioCtx: ctx };
};